import type {
    AlignControl,
    AlignOptionType,
    AnyComplexControl,
    AspectRatioControl,
    AspectRatioType,
    BackgroundControl,
    BlockSettingConfig,
    BooleanControl,
    BorderControl,
    BorderStyleControl,
    BorderStyleType,
    BorderWidthControl,
    BorderWidthType,
    ColorControl,
    ColorPaletteControl,
    ColorPaletteType,
    DividerDecoration,
    EnumControl,
    FontControl,
    FontFamilyControl,
    FontWeightControl,
    FontWeightType,
    HeightControl,
    HeightOptionType,
    IconControl,
    ImageControl,
    PaddingControl,
    PaddingOptionType,
    PixelRangeControl,
    PlacementControl,
    PlacementType,
    PositionControl,
    PositionType,
    RadioControl,
    RadioControlOptionsMeta,
    RoundnessControl,
    RoundnessOptionType,
    SelectControl,
    SelectControlOptionsMeta,
    ShadowControl,
    ShadowOptionType,
    ShapeControl,
    ShapeType,
    SizeControl,
    SizeOptionType,
    TextDecoration,
    ValueControl,
    WidthControl,
    WidthOptionType,
} from "./types";

type ThemeKey = { themeKey: string };
const isThemeKey = (value: unknown): value is ThemeKey =>
    typeof value === "object" && value !== undefined && value !== null && "themeKey" in value;

type ValueOrEnumControl<T> = ValueControl<T> | EnumControl<T>;

export const getFallback = <T>(valueOrThemeKey: T | ThemeKey): ValueOrEnumControl<T>["fallback"] => {
    if (valueOrThemeKey === undefined) {
        throw new Error("You have to provide either a default value or a theme key to a style control.");
    }

    if (isThemeKey(valueOrThemeKey)) {
        return {
            type: "THEME",
            key: valueOrThemeKey.themeKey,
        };
    }

    return {
        type: "VALUE",
        value: valueOrThemeKey,
    };
};

const DEFAULT_PADDING_OPTIONS: PaddingOptionType[] = ["AUTO", "NONE", "S", "M", "L", "XL"];
export const padding = <TValue extends TOption, TOption extends PaddingOptionType = PaddingOptionType>(
    defaultValue: TValue | ThemeKey,
    options?: TOption[],
): PaddingControl<TOption> => ({
    type: "control",
    controlType: "padding",
    options: options || (DEFAULT_PADDING_OPTIONS as TOption[]),
    value: null,
    fallback: getFallback(defaultValue),
    label: "Padding",
    key: "",
});

export const color = (defaultValue: string | ThemeKey): ColorControl => ({
    type: "control",
    controlType: "color",
    value: null,
    fallback: getFallback(defaultValue),
    label: "Color",
    key: "",
});

export const icon = (defaultValue: string | null = null): IconControl => ({
    type: "control",
    controlType: "icon",
    value: null,
    fallback: getFallback(defaultValue),
    label: "Icon",
    key: "",
});

export const image = (defaultValue: string | null = null): ImageControl => ({
    type: "control",
    controlType: "image",
    value: null,
    fallback: getFallback(defaultValue),
    label: "Image",
    key: "",
});

const DEFAULT_ROUNDNESS_OPTIONS: RoundnessOptionType[] = ["NONE", "SUBTLE", "STANDARD", "SMOOTH", "CIRCULAR"];
export const roundness = <TValue extends TOption, TOption extends RoundnessOptionType = RoundnessOptionType>(
    defaultValue: TValue | ThemeKey,
    options?: TOption[],
): RoundnessControl<TOption> => ({
    type: "control",
    controlType: "roundness",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_ROUNDNESS_OPTIONS as TOption[]),
    label: "Roundness",
    key: "",
});

const DEFAULT_SHADOW_OPTIONS: ShadowOptionType[] = ["NONE", "S", "M", "L"];
export const shadow = <TValue extends TOption, TOption extends ShadowOptionType = ShadowOptionType>(
    defaultValue: TValue | ThemeKey,
    options?: TOption[],
): ShadowControl<TOption> => ({
    type: "control",
    controlType: "shadow",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_SHADOW_OPTIONS as TOption[]),
    label: "Shadow",
    key: "",
});

const DEFAULT_SIZE_OPTIONS: SizeOptionType[] = ["S", "M", "L"];
export const size = <TValue extends TOption, TOption extends SizeOptionType = SizeOptionType>(
    defaultValue: TValue | ThemeKey,
    options?: TOption[],
): SizeControl<TOption> => ({
    type: "control",
    controlType: "size",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_SIZE_OPTIONS as TOption[]),
    label: "Size",
    key: "",
});

const DEFAULT_WIDTH_OPTIONS: WidthOptionType[] = ["XS", "S", "M", "L", "XL", "FULL"];
export const width = <TValue extends TOption, TOption extends WidthOptionType = WidthOptionType>(
    defaultValue: TValue,
    options?: TOption[],
): WidthControl<TOption> => ({
    type: "control",
    controlType: "width",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_WIDTH_OPTIONS as TOption[]),
    label: "Width",
    key: "",
});

const DEFAULT_HEIGHT_OPTIONS: HeightOptionType[] = ["XS", "S", "M", "L", "XL", "FULL"];
export const height = <TValue extends TOption, TOption extends HeightOptionType = HeightOptionType>(
    defaultValue: TValue,
    options?: TOption[],
): HeightControl<TOption> => ({
    type: "control",
    controlType: "height",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_HEIGHT_OPTIONS as TOption[]),
    label: "Height",
    key: "",
});

const DEFAULT_ALIGN_OPTIONS: AlignOptionType[] = ["start", "center", "end"];
export const align = <TValue extends TOption, TOption extends AlignOptionType = AlignOptionType>(
    defaultValue: TValue,
    options?: TOption[],
): AlignControl<TOption> => ({
    type: "control",
    controlType: "align",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_ALIGN_OPTIONS as TOption[]),
    label: "Align",
    key: "",
});

const DEFAULT_BORDER_WIDTH_OPTIONS: BorderWidthType[] = [0, 1, 2, 3, 4, 8, 16];
export const borderWidth = <TValue extends TOption, TOption extends BorderWidthType = BorderWidthType>(
    defaultValue: TValue | ThemeKey = 0 as TValue,
    options?: TOption[],
): BorderWidthControl<TOption> => ({
    type: "control",
    controlType: "borderWidth",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_BORDER_WIDTH_OPTIONS as TOption[]),
    label: "Border width",
    key: "",
});

const DEFAULT_BORDER_STYLE_OPTIONS: BorderStyleType[] = ["none", "solid", "dashed", "dotted"];
export const borderStyle = <TValue extends TOption, TOption extends BorderStyleType = BorderStyleType>(
    defaultValue = "none" as TValue,
    options?: TOption[],
): BorderStyleControl<TOption> => ({
    type: "control",
    controlType: "borderStyle",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_BORDER_STYLE_OPTIONS as TOption[]),
    label: "Border style",
    key: "",
});

export const fontFamily = (defaultValue: string | ThemeKey): FontFamilyControl => ({
    type: "control",
    controlType: "fontFamily",
    value: null,
    fallback: getFallback(defaultValue),
    label: "Font family",
    key: "",
});

const DEFAULT_FONT_WEIGHT_OPTIONS: FontWeightType[] = [100, 200, 300, 400, 500, 600, 700, 800, 900];
const FONT_WEIGHT_META: Record<FontWeightType, { label: string }> = {
    100: { label: "Hairline" },
    200: { label: "Thin" },
    300: { label: "Light" },
    400: { label: "Normal" },
    500: { label: "Medium" },
    600: { label: "Semibold" },
    700: { label: "Bold" },
    800: { label: "Extrabold" },
    900: { label: "Black" },
};
export const fontWeight = <TValue extends TOption, TOption extends FontWeightType = FontWeightType>(
    defaultValue: TValue | ThemeKey,
    options?: TOption[],
): FontWeightControl<TOption> => ({
    type: "control",
    controlType: "fontWeight",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_FONT_WEIGHT_OPTIONS as TOption[]),
    optionsMeta: FONT_WEIGHT_META,
    label: "Font weight",
    key: "",
});

export const boolean = (defaultValue: boolean | ThemeKey): BooleanControl => ({
    type: "control",
    controlType: "boolean",
    value: null,
    fallback: getFallback(defaultValue),
    key: "",
    label: "Toggle",
});

const DEFAULT_COLOR_PALETTE_OPTIONS: ColorPaletteType[] = [
    "NONE",
    "DATASOURCE",
    "SOFTR",
    "BALANCEDNATURE",
    "SOFTSUNSET",
    "NEONDREAMS",
];
export const colorPalette = <TValue extends TOption, TOption extends ColorPaletteType = ColorPaletteType>(
    defaultValue: TValue,
    options?: TOption[],
): ColorPaletteControl<TOption> => ({
    type: "control",
    controlType: "colorPalette",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_COLOR_PALETTE_OPTIONS as TOption[]),
    label: "Style",
    key: "",
});

const DEFAULT_ASPECT_RATIO_OPTIONS: AspectRatioType[] = ["1:1", "4:3", "16:9", "AUTO"];
export const aspectRatio = <TValue extends TOption, TOption extends AspectRatioType>(
    defaultValue: TValue,
    options?: TOption[],
): AspectRatioControl<TOption> => ({
    type: "control",
    controlType: "aspectRatio",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_ASPECT_RATIO_OPTIONS as TOption[]),
    label: "Aspect ratio",
    key: "",
});

const DEFAULT_POSITION_OPTIONS: PositionType[] = ["TOP_LEFT", "TOP_RIGHT", "BOTTOM_LEFT", "BOTTOM_RIGHT"];
export const position = <TValue extends TOption, TOption extends PositionType>(
    defaultValue: TValue,
    options?: TOption[],
): PositionControl<TOption> => ({
    type: "control",
    controlType: "position",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_POSITION_OPTIONS as TOption[]),
    label: "Position",
    key: "",
});

const DEFAULT_PLACEMENT_OPTIONS: PlacementType[] = ["LEFT", "TOP", "RIGHT", "BOTTOM"];
export const placement = <TValue extends TOption, TOption extends PlacementType>(
    defaultValue: TValue,
    options?: TOption[],
): PlacementControl<TOption> => ({
    type: "control",
    controlType: "placement",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_PLACEMENT_OPTIONS as TOption[]),
    label: "Placement",
    key: "",
});

const DEFAULT_SHAPE_OPTIONS: ShapeType[] = ["STAR", "HEART"];
export const shape = <TValue extends TOption, TOption extends ShapeType>(
    defaultValue: TValue,
    options?: TOption[],
): ShapeControl<TOption> => ({
    type: "control",
    controlType: "shape",
    value: null,
    fallback: getFallback(defaultValue),
    options: options || (DEFAULT_SHAPE_OPTIONS as TOption[]),
    label: "Shape",
    key: "",
});

export const select = <TValue extends TOption, const TOption extends string>(
    defaultValue: TValue,
    options: Array<{ value: TOption } & SelectControlOptionsMeta>,
): SelectControl<TOption> => ({
    type: "control",
    controlType: "select",
    value: null,
    fallback: getFallback(defaultValue),
    options: options.map((option) => option.value),
    optionsMeta: Object.fromEntries(options.map(({ value, ...meta }) => [value, meta])) as Record<
        TOption,
        SelectControlOptionsMeta
    >,
    label: "Select",
    key: "",
});

export const pixelRange = (config: {
    defaultValue: number;
    min: number;
    max: number;
    step: number;
}): PixelRangeControl => ({
    type: "control",
    controlType: "pixelRange",
    value: null,
    fallback: {
        type: "VALUE",
        value: config.defaultValue,
    },
    min: config.min,
    max: config.max,
    step: config.step,
    key: "",
    label: "Size",
});

export const radio = <TValue extends TOption, const TOption extends string>(
    { defaultValue, ...rest }: { defaultValue: TValue; info?: string },
    options: Array<{ value: TOption } & RadioControlOptionsMeta>,
): RadioControl<TOption> => ({
    type: "control",
    controlType: "radio",
    value: null,
    fallback: getFallback(defaultValue),
    ...rest,
    options: options.map((option) => option.value),
    optionsMeta: Object.fromEntries(options.map(({ value, ...meta }) => [value, meta])) as Record<
        TOption,
        RadioControlOptionsMeta
    >,
    label: "Radio",
    key: "",
});

const validateComplexStyles = <T extends AnyComplexControl["styles"]>(
    controlType: string,
    styles: T,
    allowedControlTypes: string[],
) => {
    const hasForbiddenControlTypes = styles.some((style) => !allowedControlTypes.includes(style.controlType));
    if (hasForbiddenControlTypes) {
        throw new Error(
            `${controlType} may only contain ${allowedControlTypes
                .map((type) => JSON.stringify(type))
                .join(", ")} controls inside styles`,
        );
    }
};

interface ComplexStyleParams<T extends AnyComplexControl> {
    controlType: T["controlType"];
    allowed: T["styles"][number]["controlType"][];
    label: string;
}

const makeComplexStyle =
    <T extends AnyComplexControl>({ controlType, allowed, label }: ComplexStyleParams<T>) =>
    (styles: T["styles"], config: BlockSettingConfig = {}): T => {
        validateComplexStyles(controlType, styles, allowed);

        return {
            type: "control",
            controlType,
            label,
            styles,
            ...config,
        } as T;
    };

export const borderControl = makeComplexStyle<BorderControl>({
    controlType: "border",
    allowed: ["borderStyle", "borderWidth", "color"],
    label: "Border",
});

export const backgroundControl = makeComplexStyle<BackgroundControl>({
    controlType: "background",
    allowed: ["select", "color", "image"],
    label: "Background",
});

export const fontControl = makeComplexStyle<FontControl>({
    controlType: "font",
    allowed: ["fontFamily", "color", "fontWeight"],
    label: "Font",
});

export const text = (
    text: string,
    config: Omit<TextDecoration, "label" | "type" | "decorationType">,
): TextDecoration => ({
    type: "decoration",
    decorationType: "text",
    label: text,
    ...config,
});
export const divider = (): DividerDecoration => ({
    type: "decoration",
    decorationType: "divider",
    label: "",
});
